import { redirect, type ActionFunctionArgs } from "react-router";
import { supabase } from "@shared/integrations/supabase/client";
import { invokeFunction } from "@shared/functions.ts";
import {
  formatSupabaseFunctionError,
  isSupabaseResponseError,
} from "@shared/functions/supabaseFunctionError.ts";
import StartTestRunContract from "@shared/functions/start-test-run.ts";
import { juryConfigSchema } from "@shared/judge/schema";
import type { Database } from "@/integrations/supabase/types";

export const WORKSPACE_SIDEBAR_ACTION_PATH = "/workspace/sidebar-action";

const DEFAULT_TITLES = {
  project: "New Eval Project",
  agent: "New Agent",
  judge: "New Judge",
  code: "New Agent",
  dataset: "New Dataset",
} as const;

const NAVIGATE_TARGETS = {
  project: (id: string) => `/workspace/evals/${id}`,
  // agent: (id: string) => `/workspace/agents/${id}`,
  judge: (id: string) => `/workspace/judges/${id}`,
  code: (id: string) => `/workspace/codes/${id}`,
  dataset: (id: string) => `/dataset/${id}`,
} as const;

const CODE_SNIPPET = "return function(input) { return input }";

const ensureUser = async () => {
  const { data, error } = await supabase.auth.getUser();
  if (error || !data?.user) {
    throw redirect("/auth");
  }
  return data.user;
};

const ensureAdminUser = async () => {
  const { data, error } = await supabase.rpc("is_admin");

  if (error) {
    throw new Response(error.message ?? "Failed to verify admin privileges", {
      status: 500,
    });
  }

  if (!data) {
    throw new Response("Admin privileges are required to update this setting", {
      status: 403,
    });
  }
};

const getFormValue = (formData: FormData, key: string) => {
  const value = formData.get(key);
  return typeof value === "string" ? value : undefined;
};

const getRequiredValue = (formData: FormData, key: string) => {
  const value = getFormValue(formData, key);
  if (!value) {
    throw new Response(`${key} is required`, { status: 400 });
  }
  return value;
};

const getRequiredBoolean = (formData: FormData, key: string) => {
  const value = getRequiredValue(formData, key);
  if (value !== "true" && value !== "false") {
    throw new Response(`${key} must be true or false`, { status: 400 });
  }
  return value === "true";
};

const updateTitle = async (
  table: "projects" | "agents" | "judges" | "codes",
  id: string,
  title: string,
) => {
  const trimmed = title.trim();
  if (!trimmed) {
    throw new Response("Title is required", { status: 400 });
  }

  const { error } = await supabase
    .from(table)
    .update({ title: trimmed })
    .eq("id", id);

  if (error) {
    throw new Response(error.message ?? "Failed to update record", {
      status: 500,
    });
  }

  return { recordId: id };
};

const createProjectRecord = async (formData: FormData, userId: string) => {
  const title = getFormValue(formData, "title") ?? DEFAULT_TITLES.project;

  const { data, error } = await supabase
    .from("projects")
    .insert([{ user_id: userId, title }])
    .select()
    .single();

  if (error || !data) {
    throw new Response(error?.message ?? "Failed to create project", {
      status: 500,
    });
  }
  const id = String(data.id);
  return { recordId: id, navigateTo: NAVIGATE_TARGETS.project(id) };
};

// LLM agent actions are disabled for v1.1 demo mode.
/*
const createAgentRecord = async (formData: FormData, userId: string) => {
  const title = getFormValue(formData, "title") ?? DEFAULT_TITLES.agent;

  const { data, error } = await supabase
    .from("agents")
    .insert([{ user_id: userId, title }])
    .select()
    .single();

  if (error || !data) {
    throw new Response(error?.message ?? "Failed to create agent", {
      status: 500,
    });
  }
  const id = String(data.id);
  return { recordId: id, navigateTo: NAVIGATE_TARGETS.agent(id) };
};
*/

const createJudgeRecord = async (formData: FormData, userId: string) => {
  const title = getFormValue(formData, "title") ?? DEFAULT_TITLES.judge;

  const { data, error } = await supabase
    .from("judges")
    .insert([{ user_id: userId, title }])
    .select()
    .single();

  if (error || !data) {
    throw new Response(error?.message ?? "Failed to create judge", {
      status: 500,
    });
  }
  const id = String(data.id);
  return { recordId: id, navigateTo: NAVIGATE_TARGETS.judge(id) };
};

const createCodeRecord = async (formData: FormData, userId: string) => {
  const title = getFormValue(formData, "title") ?? DEFAULT_TITLES.code;

  const { data, error } = await supabase
    .from("codes")
    .insert([
      {
        user_id: userId,
        title,
        code_content: CODE_SNIPPET,
      },
    ])
    .select()
    .single();

  if (error || !data) {
    throw new Response(error?.message ?? "Failed to create agent", {
      status: 500,
    });
  }
  const id = String(data.id);
  return { recordId: id, navigateTo: NAVIGATE_TARGETS.code(id) };
};

const createDatasetRecord = async (formData: FormData, userId: string) => {
  const name = getFormValue(formData, "name") ?? DEFAULT_TITLES.dataset;
  const datasetKindRaw = getFormValue(formData, "dataset_kind");
  const creationMethod = getFormValue(formData, "creation_method") ?? "Manual";

  const datasetKind: Database["public"]["Enums"]["dataset_kind"] =
    datasetKindRaw === "operational_rules"
      ? "operational_rules"
      : "behavior_examples";

  const { data, error } = await supabase
    .from("test_data_sets")
    .insert([
      {
        user_id: userId,
        name,
        type: "primary",
        dataset_kind: datasetKind,
        creation_method: creationMethod,
      },
    ])
    .select()
    .single();

  if (error || !data) {
    throw new Response(error?.message ?? "Failed to create dataset", {
      status: 500,
    });
  }
  const id = String(data.id);
  return { recordId: id, navigateTo: NAVIGATE_TARGETS.dataset(id) };
};

type TestRunEvalType = "Strict Equality" | "Any permitted" | "LLM-As-A-Judge";

const getEvalType = (value: string): TestRunEvalType => {
  const supported: TestRunEvalType[] = [
    "Strict Equality",
    "Any permitted",
    "LLM-As-A-Judge",
  ];
  if ((supported as string[]).includes(value)) {
    return value as TestRunEvalType;
  }
  throw new Response("Unsupported evaluation type", { status: 400 });
};

const createTestRunRecord = async (formData: FormData, userId: string) => {
  const projectId = getRequiredValue(formData, "projectId");
  const testDataSetId = getRequiredValue(formData, "testDataSetId");
  const evalType = getEvalType(getRequiredValue(formData, "evalType"));
  const permittedValuesRaw = getFormValue(formData, "permittedValues");
  const judgeId = getFormValue(formData, "judgeId");

  if (evalType === "Any permitted" && !permittedValuesRaw?.trim()) {
    throw new Response(
      'Permitted values are required for "Any permitted" evaluation',
      {
        status: 400,
      },
    );
  }

  if (evalType === "LLM-As-A-Judge" && !judgeId) {
    throw new Response(
      "Judge selection is required for LLM-as-a-Judge evaluation",
      {
        status: 400,
      },
    );
  }

  // Fetch dataset details including kind and parent
  const { data: dataSet, error: dataSetError } = await supabase
    .from("test_data_sets")
    .select("id, type, dataset_kind, creation_method, parent_id")
    .eq("id", testDataSetId)
    .single();

  if (dataSetError || !dataSet) {
    throw new Response(dataSetError?.message ?? "Test data set not found", {
      status: 404,
    });
  }

  const testRunType: Database["public"]["Enums"]["test_run_type"] =
    dataSet.type === "primary" ? "primary" : "adversarial";

  const permittedValues =
    evalType === "Any permitted" && permittedValuesRaw
      ? permittedValuesRaw.trim()
      : null;

  const normalizedJudgeId =
    evalType === "LLM-As-A-Judge" ? (judgeId ?? null) : null;

  // Adversarial gating: require a completed primary run before adversarial runs
  let requiresPrimaryComplete = false;
  let primaryRunRequiredId: string | null = null;
  let parentDataSet: {
    dataset_kind: Database["public"]["Enums"]["dataset_kind"];
  } | null = null;

  if (dataSet.type === "adversarial" && dataSet.parent_id) {
    // Fetch parent dataset to determine gating rules
    const { data: fetchedParent, error: parentError } = await supabase
      .from("test_data_sets")
      .select("dataset_kind")
      .eq("id", dataSet.parent_id)
      .single();

    parentDataSet = fetchedParent;

    if (parentError || !parentDataSet) {
      throw new Response("Parent dataset not found", { status: 404 });
    }

    requiresPrimaryComplete = true;

    // Find the most recent completed primary run for the parent dataset
    const { data: primaryRun, error: primaryRunError } = await supabase
      .from("test_runs")
      .select("id, status")
      .eq("project_id", projectId)
      .eq("test_data_set_id", dataSet.parent_id)
      .eq("type", "primary")
      .eq("status", "Complete")
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (primaryRunError) {
      throw new Response("Error checking primary run status", {
        status: 500,
      });
    }

    if (!primaryRun) {
      throw new Response(
        "A completed primary run is required before running an adversarial test",
        { status: 400 },
      );
    }

    primaryRunRequiredId = primaryRun.id;
  }

  const payload: Database["public"]["Tables"]["test_runs"]["Insert"] = {
    project_id: projectId,
    test_data_set_id: testDataSetId,
    user_id: userId,
    type: testRunType,
    status: "Prepared",
    start_time: new Date().toISOString(),
    eval_type: evalType,
    permitted_values: permittedValues,
    judge_id: normalizedJudgeId,
    dataset_kind_snapshot:
      dataSet.type === "adversarial" && parentDataSet
        ? parentDataSet.dataset_kind
        : dataSet.dataset_kind,
    creation_method_snapshot: dataSet.creation_method,
    requires_primary_complete: requiresPrimaryComplete,
    primary_run_required_id: primaryRunRequiredId,
  };

  const { data, error } = await supabase
    .from("test_runs")
    .insert([payload])
    .select("id")
    .single();

  if (error || !data) {
    console.error("Test run creation error:", {
      code: error?.code,
      message: error?.message,
      details: error?.details,
      hint: error?.hint,
    });

    let errorMessage = error?.message ?? "Failed to create test run";

    if (error?.code === "42501") {
      errorMessage +=
        ". You may not have permission to create test runs for this project.";
    }

    if (error?.details) {
      errorMessage += ` Details: ${error.details}`;
    }

    if (error?.hint) {
      errorMessage += ` Hint: ${error.hint}`;
    }

    throw new Response(errorMessage, { status: 500 });
  }

  return { recordId: String(data.id) };
};

const launchTestRun = async (formData: FormData) => {
  const testRunId = getRequiredValue(formData, "testRunId");

  // Get the current session to pass auth to the edge function
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session) {
    throw new Response("Not authenticated", { status: 401 });
  }

  const result = await invokeFunction(StartTestRunContract, {
    client: supabase,
    body: { testRunId },
    headers: {
      Authorization: `Bearer ${session.access_token}`,
    },
  });

  if (!result.ok) {
    const status = isSupabaseResponseError(result.error)
      ? (result.error.status ?? 500)
      : 500;

    throw new Response(formatSupabaseFunctionError(result.error), {
      status,
    });
  }

  return { recordId: testRunId };
};

const deleteTestRunRecord = async (formData: FormData) => {
  const testRunId = getRequiredValue(formData, "testRunId");

  const { error } = await supabase
    .from("test_runs")
    .delete()
    .eq("id", testRunId);

  if (error) {
    throw new Response(error.message ?? "Failed to delete test run", {
      status: 500,
    });
  }

  return { recordId: testRunId };
};

/*
const updateAgentSystemPrompt = async (
  agentId: string,
  systemPrompt: string,
) => {
  const { error } = await supabase
    .from("agents")
    .update({ system_prompt: systemPrompt })
    .eq("id", agentId);

  if (error) {
    throw new Response(error.message ?? "Failed to update system prompt", {
      status: 500,
    });
  }

  return { recordId: agentId };
};

const updateAgentVisibility = async (agentId: string, isPublic: boolean) => {
  const { error } = await supabase
    .from("agents")
    .update({ is_public: isPublic })
    .eq("id", agentId);

  if (error) {
    throw new Response(error.message ?? "Failed to update visibility", {
      status: 500,
    });
  }

  return { recordId: agentId };
};

const updateAgentCredentials = async (formData: FormData, _userId: string) => {
  const agentId = getRequiredValue(formData, "entityId");
  const model = getRequiredValue(formData, "model");
  const maxTokensValue = Number(getRequiredValue(formData, "maxTokens"));
  const temperatureValue = Number(getRequiredValue(formData, "temperature"));

  if (Number.isNaN(maxTokensValue) || Number.isNaN(temperatureValue)) {
    throw new Response("Invalid numeric configuration", { status: 400 });
  }

  const { error } = await supabase
    .from("agents")
    .update({
      model,
      max_tokens: maxTokensValue,
      temperature: temperatureValue,
    })
    .eq("id", agentId);

  if (error) {
    throw new Response(
      error.message ?? "Failed to update agent configuration",
      { status: 500 },
    );
  }

  return { recordId: agentId };
};
*/

const updateUserProfileSettings = async (
  userId: string,
  displayName: string | undefined,
  preferredModel: string | undefined,
) => {
  const { error } = await supabase
    .from("profiles")
    .upsert(
      [
        {
          user_id: userId,
          display_name: displayName?.trim() || null,
          preferred_model: preferredModel ?? "openai/gpt-4o-mini",
        },
      ],
      { onConflict: "user_id" },
    )
    .select()
    .maybeSingle();

  if (error) {
    throw new Response(error.message ?? "Failed to update profile", {
      status: 500,
    });
  }

  return { recordId: userId };
};

const updateUserPassword = async (newPassword: string) => {
  const { error } = await supabase.auth.updateUser({ password: newPassword });

  if (error) {
    throw new Response(error.message ?? "Failed to update password", {
      status: 400,
    });
  }

  return { recordId: "password" };
};

/*
const duplicateAgent = async (formData: FormData, userId: string) => {
  const title = getRequiredValue(formData, "title");
  const model = getFormValue(formData, "model") ?? "openai/gpt-4o-mini";
  const maxTokensRaw = getFormValue(formData, "max_tokens");
  const temperatureRaw = getFormValue(formData, "temperature");
  const systemPrompt = getFormValue(formData, "system_prompt") ?? "";

  const maxTokens = maxTokensRaw ? Number(maxTokensRaw) : null;
  const temperature = temperatureRaw ? Number(temperatureRaw) : null;

  const { data, error } = await supabase
    .from("agents")
    .insert([
      {
        user_id: userId,
        title,
        model,
        max_tokens: Number.isFinite(maxTokens) ? maxTokens : null,
        temperature: Number.isFinite(temperature) ? temperature : null,
        system_prompt: systemPrompt,
        openrouter_api_key_configured: false,
      },
    ])
    .select()
    .single();

  if (error || !data) {
    throw new Response(error?.message ?? "Failed to duplicate agent", {
      status: 500,
    });
  }

  const id = String(data.id);
  return { recordId: id, navigateTo: NAVIGATE_TARGETS.agent(id) };
};

const deleteAgent = async (agentId: string) => {
  const { error } = await supabase.from("agents").delete().eq("id", agentId);
  if (error) {
    throw new Response(error.message ?? "Failed to delete agent", {
      status: 500,
    });
  }
  return { recordId: agentId, navigateTo: "/agents" };
};
*/

const updateCodeContent = async (codeId: string, codeContent: string) => {
  const { error } = await supabase
    .from("codes")
    .update({ code_content: codeContent })
    .eq("id", codeId);

  if (error) {
    throw new Response(error.message ?? "Failed to save agent", { status: 500 });
  }

  return { recordId: codeId };
};

const updateCodeVisibility = async (codeId: string, isPublic: boolean) => {
  const { error } = await supabase
    .from("codes")
    .update({ is_public: isPublic })
    .eq("id", codeId);

  if (error) {
    throw new Response(error.message ?? "Failed to update agent visibility", {
      status: 500,
    });
  }

  return { recordId: codeId };
};

const updateCodeSandbox = async (codeId: string, sandbox: boolean) => {
  await ensureAdminUser();

  const { error } = await supabase
    .from("codes")
    .update({ sandbox })
    .eq("id", codeId);

  if (error) {
    throw new Response(error.message ?? "Failed to update sandbox mode", {
      status: 500,
    });
  }

  return { recordId: codeId };
};

const deleteCode = async (codeId: string) => {
  const { error } = await supabase.from("codes").delete().eq("id", codeId);
  if (error) {
    throw new Response(error.message ?? "Failed to delete agent", {
      status: 500,
    });
  }
  return { recordId: codeId, navigateTo: "/codes" };
};

const updateJudgeSystemPrompt = async (
  judgeId: string,
  systemPrompt: string,
) => {
  const { error } = await supabase
    .from("judges")
    .update({ system_prompt: systemPrompt })
    .eq("id", judgeId);

  if (error) {
    throw new Response(error.message ?? "Failed to update system prompt", {
      status: 500,
    });
  }

  return { recordId: judgeId };
};

const updateJudgeVisibility = async (judgeId: string, isPublic: boolean) => {
  const { error } = await supabase
    .from("judges")
    .update({ is_public: isPublic })
    .eq("id", judgeId);

  if (error) {
    throw new Response(error.message ?? "Failed to update visibility", {
      status: 500,
    });
  }

  return { recordId: judgeId };
};

const updateJudgeJuryConfig = async (
  judgeId: string,
  juryConfig: unknown,
) => {
  const parsedConfig = juryConfigSchema.safeParse(juryConfig);
  if (!parsedConfig.success) {
    throw new Response("Invalid juryConfig", { status: 400 });
  }

  const { error } = await supabase
    .from("judges")
    .update({ jury_config: parsedConfig.data })
    .eq("id", judgeId);

  if (error) {
    throw new Response(error.message ?? "Failed to update jury config", {
      status: 500,
    });
  }

  return { recordId: judgeId };
};

const duplicateJudge = async (formData: FormData, userId: string) => {
  const title = getRequiredValue(formData, "title");
  const model = getFormValue(formData, "model") ?? "openai/gpt-4o-mini";
  const maxTokensRaw = getFormValue(formData, "max_tokens");
  const temperatureRaw = getFormValue(formData, "temperature");
  const systemPrompt = getFormValue(formData, "system_prompt") ?? "";

  const maxTokens = maxTokensRaw ? Number(maxTokensRaw) : null;
  const temperature = temperatureRaw ? Number(temperatureRaw) : null;

  const { data, error } = await supabase
    .from("judges")
    .insert([
      {
        user_id: userId,
        title,
        model,
        max_tokens: Number.isFinite(maxTokens) ? maxTokens : null,
        temperature: Number.isFinite(temperature) ? temperature : null,
        system_prompt: systemPrompt,
        openrouter_api_key_configured: false,
      },
    ])
    .select()
    .single();

  if (error || !data) {
    throw new Response(error?.message ?? "Failed to duplicate judge", {
      status: 500,
    });
  }

  const id = String(data.id);
  return { recordId: id, navigateTo: NAVIGATE_TARGETS.judge(id) };
};

const deleteJudge = async (judgeId: string) => {
  const { error } = await supabase.from("judges").delete().eq("id", judgeId);
  if (error) {
    throw new Response(error.message ?? "Failed to delete judge", {
      status: 500,
    });
  }
  return { recordId: judgeId, navigateTo: "/judges" };
};

const assignProjectConfiguration = async (
  projectId: string,
  agentId: string | null,
  codeId: string | null,
) => {
  // Enforce mutual exclusivity - only one can be set
  if (agentId && codeId) {
    throw new Response("Cannot assign both agent and code to a project", {
      status: 400,
    });
  }

  const { error } = await supabase
    .from("projects")
    .update({
      agent_id: agentId,
      code_id: codeId,
    })
    .eq("id", projectId);

  if (error) {
    throw new Response(error.message ?? "Failed to assign configuration", {
      status: 500,
    });
  }

  return { recordId: projectId };
};

const assignProjectBehaviorDataset = async (
  projectId: string,
  datasetId: string | null,
) => {
  if (datasetId) {
    const { data: dataset, error: datasetError } = await supabase
      .from("test_data_sets")
      .select("dataset_kind, type")
      .eq("id", datasetId)
      .single();

    if (datasetError || !dataset) {
      throw new Response("Dataset not found", { status: 404 });
    }

    if (dataset.dataset_kind !== "behavior_examples") {
      throw new Response(
        "Only behavior_examples datasets can be assigned as behavior dataset",
        {
          status: 400,
        },
      );
    }

    if (dataset.type !== "primary") {
      throw new Response("Only primary datasets can be assigned to projects", {
        status: 400,
      });
    }
  }

  const { error } = await supabase
    .from("projects")
    .update({
      behavior_dataset_id: datasetId,
      behavior_dataset_selected_at: datasetId ? new Date().toISOString() : null,
    })
    .eq("id", projectId);

  if (error) {
    throw new Response(error.message ?? "Failed to assign behavior dataset", {
      status: 500,
    });
  }

  return { recordId: projectId };
};

const assignProjectRulesDataset = async (
  projectId: string,
  datasetId: string | null,
) => {
  if (datasetId) {
    const { data: dataset, error: datasetError } = await supabase
      .from("test_data_sets")
      .select("dataset_kind, type")
      .eq("id", datasetId)
      .single();

    if (datasetError || !dataset) {
      throw new Response("Dataset not found", { status: 404 });
    }

    if (dataset.dataset_kind !== "operational_rules") {
      throw new Response(
        "Only operational_rules datasets can be assigned as rules dataset",
        {
          status: 400,
        },
      );
    }

    if (dataset.type !== "primary") {
      throw new Response("Only primary datasets can be assigned to projects", {
        status: 400,
      });
    }
  }

  const { error } = await supabase
    .from("projects")
    .update({
      rules_dataset_id: datasetId,
      rules_dataset_selected_at: datasetId ? new Date().toISOString() : null,
    })
    .eq("id", projectId);

  if (error) {
    throw new Response(error.message ?? "Failed to assign rules dataset", {
      status: 500,
    });
  }

  return { recordId: projectId };
};

const updateProjectVisibility = async (projectId: string, isPublic: boolean) => {
  const { error } = await supabase
    .from("projects")
    .update({ is_public: isPublic })
    .eq("id", projectId);

  if (error) {
    throw new Response(error.message ?? "Failed to update eval visibility", {
      status: 500,
    });
  }

  return { recordId: projectId };
};

const deleteProject = async (projectId: string) => {
  const { error } = await supabase
    .from("projects")
    .delete()
    .eq("id", projectId);
  if (error) {
    throw new Response(error.message ?? "Failed to delete project", {
      status: 500,
    });
  }
  throw redirect("/evals");
};

const actionHandlers = {
  createProject: async (formData: FormData, userId: string) =>
    createProjectRecord(formData, userId),
  createJudge: async (formData: FormData, userId: string) =>
    createJudgeRecord(formData, userId),
  createCode: async (formData: FormData, userId: string) =>
    createCodeRecord(formData, userId),
  createDataset: async (formData: FormData, userId: string) =>
    createDatasetRecord(formData, userId),
  updateProjectTitle: async (formData: FormData, _userId: string) => {
    const entityId = getRequiredValue(formData, "entityId");
    const title = getRequiredValue(formData, "title");
    return updateTitle("projects", entityId, title);
  },
  projectTogglePublic: async (formData: FormData) => {
    const entityId = getRequiredValue(formData, "entityId");
    const isPublic = getRequiredBoolean(formData, "isPublic");
    return updateProjectVisibility(entityId, isPublic);
  },
  updateJudgeTitle: async (formData: FormData, _userId: string) => {
    const entityId = getRequiredValue(formData, "entityId");
    const title = getRequiredValue(formData, "title");
    return updateTitle("judges", entityId, title);
  },
  updateCodeTitle: async (formData: FormData, _userId: string) => {
    const entityId = getRequiredValue(formData, "entityId");
    const title = getRequiredValue(formData, "title");
    return updateTitle("codes", entityId, title);
  },
  // LLM agent actions are disabled for v1.1 demo mode.
  // createAgent: async (formData: FormData, userId: string) =>
  //   createAgentRecord(formData, userId),
  // updateAgentTitle: async (formData: FormData, _userId: string) => {
  //   const entityId = getRequiredValue(formData, "entityId");
  //   const title = getRequiredValue(formData, "title");
  //   return updateTitle("agents", entityId, title);
  // },
  // agentUpdateSystemPrompt: async (formData: FormData, _userId: string) => {
  //   const entityId = getRequiredValue(formData, "entityId");
  //   const prompt = getFormValue(formData, "systemPrompt") ?? "";
  //   return updateAgentSystemPrompt(entityId, prompt);
  // },
  // agentTogglePublic: async (formData: FormData, _userId: string) => {
  //   const entityId = getRequiredValue(formData, "entityId");
  //   const isPublic = getRequiredBoolean(formData, "isPublic");
  //   return updateAgentVisibility(entityId, isPublic);
  // },
  // agentDuplicate: async (formData: FormData, userId: string) =>
  //   duplicateAgent(formData, userId),
  // agentUpdateTitle: async (formData: FormData, _userId: string) => {
  //   const entityId = getRequiredValue(formData, "entityId");
  //   const title = getRequiredValue(formData, "title");
  //   return updateTitle("agents", entityId, title);
  // },
  // agentUpdateCredentials: async (formData: FormData, userId: string) =>
  //   updateAgentCredentials(formData, userId),
  // agentDelete: async (formData: FormData) => {
  //   const entityId = getRequiredValue(formData, "entityId");
  //   return deleteAgent(entityId);
  // },
  codeUpdateContent: async (formData: FormData) => {
    const entityId = getRequiredValue(formData, "entityId");
    const codeContent = getFormValue(formData, "codeContent") ?? "";
    return updateCodeContent(entityId, codeContent);
  },
  codeTogglePublic: async (formData: FormData) => {
    const entityId = getRequiredValue(formData, "entityId");
    const isPublic = getRequiredBoolean(formData, "isPublic");
    return updateCodeVisibility(entityId, isPublic);
  },
  codeToggleSandbox: async (formData: FormData) => {
    const entityId = getRequiredValue(formData, "entityId");
    const sandbox = getRequiredBoolean(formData, "sandbox");
    return updateCodeSandbox(entityId, sandbox);
  },
  codeDelete: async (formData: FormData) => {
    const entityId = getRequiredValue(formData, "entityId");
    return deleteCode(entityId);
  },
  judgeUpdateSystemPrompt: async (formData: FormData, _userId: string) => {
    const entityId = getRequiredValue(formData, "entityId");
    const prompt = getFormValue(formData, "systemPrompt") ?? "";
    return updateJudgeSystemPrompt(entityId, prompt);
  },
  judgeUpdateJuryConfig: async (formData: FormData) => {
    const entityId = getRequiredValue(formData, "entityId");
    const configRaw = getRequiredValue(formData, "juryConfig");

    let parsed: unknown;
    try {
      parsed = JSON.parse(configRaw);
    } catch {
      throw new Response("juryConfig must be valid JSON", { status: 400 });
    }

    return updateJudgeJuryConfig(entityId, parsed);
  },
  judgeTogglePublic: async (formData: FormData, _userId: string) => {
    const entityId = getRequiredValue(formData, "entityId");
    const isPublic = getRequiredBoolean(formData, "isPublic");
    return updateJudgeVisibility(entityId, isPublic);
  },
  judgeDuplicate: async (formData: FormData, userId: string) =>
    duplicateJudge(formData, userId),
  judgeDelete: async (formData: FormData) => {
    const entityId = getRequiredValue(formData, "entityId");
    return deleteJudge(entityId);
  },
  projectAssignConfiguration: async (formData: FormData) => {
    const entityId = getRequiredValue(formData, "entityId");
    const agentIdRaw = getFormValue(formData, "agentId");
    const codeIdRaw = getFormValue(formData, "codeId");

    const agentId = agentIdRaw && agentIdRaw !== "none" ? agentIdRaw : null;
    const codeId = codeIdRaw && codeIdRaw !== "none" ? codeIdRaw : null;

    return assignProjectConfiguration(entityId, agentId, codeId);
  },
  projectAssignBehaviorDataset: async (formData: FormData) => {
    const entityId = getRequiredValue(formData, "entityId");
    const datasetIdRaw = getFormValue(formData, "datasetId");
    const datasetId =
      datasetIdRaw && datasetIdRaw !== "none" ? datasetIdRaw : null;
    return assignProjectBehaviorDataset(entityId, datasetId);
  },
  projectAssignRulesDataset: async (formData: FormData) => {
    const entityId = getRequiredValue(formData, "entityId");
    const datasetIdRaw = getFormValue(formData, "datasetId");
    const datasetId =
      datasetIdRaw && datasetIdRaw !== "none" ? datasetIdRaw : null;
    return assignProjectRulesDataset(entityId, datasetId);
  },
  projectDelete: async (formData: FormData) => {
    const entityId = getRequiredValue(formData, "entityId");
    return deleteProject(entityId);
  },
  userUpdateProfile: async (formData: FormData, userId: string) =>
    updateUserProfileSettings(
      userId,
      getFormValue(formData, "displayName"),
      getFormValue(formData, "preferredModel"),
    ),
  userUpdatePassword: async (formData: FormData) => {
    const password = getRequiredValue(formData, "newPassword");
    return updateUserPassword(password);
  },
  testRunCreate: async (formData: FormData, userId: string) =>
    createTestRunRecord(formData, userId),
  testRunLaunch: async (formData: FormData) => launchTestRun(formData),
  testRunDelete: async (formData: FormData) => deleteTestRunRecord(formData),
} as const;

export type WorkspaceSidebarActionIntent = keyof typeof actionHandlers;

export type LegacyAgentActionIntent =
  | "createAgent"
  | "updateAgentTitle"
  | "agentUpdateSystemPrompt"
  | "agentTogglePublic"
  | "agentDuplicate"
  | "agentUpdateTitle"
  | "agentUpdateCredentials"
  | "agentDelete";

export type WorkspaceSidebarActionResultIntent =
  | WorkspaceSidebarActionIntent
  | LegacyAgentActionIntent;

export type WorkspaceSidebarActionSuccess = {
  ok: true;
  intent: WorkspaceSidebarActionResultIntent;
  recordId: string;
  navigateTo?: string;
};

export type WorkspaceSidebarActionError = {
  ok: false;
  intent: WorkspaceSidebarActionResultIntent | "unknown";
  error: string;
};

export type WorkspaceSidebarActionResult =
  | WorkspaceSidebarActionSuccess
  | WorkspaceSidebarActionError;

const isWorkspaceSidebarActionIntent = (
  value: string,
): value is WorkspaceSidebarActionIntent => value in actionHandlers;

export const workspaceSidebarAction = async ({
  request,
}: ActionFunctionArgs): Promise<WorkspaceSidebarActionResult> => {
  const formData = await request.formData();
  const intentValue = formData.get("intent");

  if (
    typeof intentValue !== "string" ||
    !isWorkspaceSidebarActionIntent(intentValue)
  ) {
    return {
      ok: false,
      intent: "unknown",
      error: "Unsupported workspace intent",
    } satisfies WorkspaceSidebarActionError;
  }

  const user = await ensureUser();
  const handler = actionHandlers[intentValue];

  try {
    const result = await handler(formData, user.id);
    return {
      ok: true,
      intent: intentValue,
      ...result,
    } satisfies WorkspaceSidebarActionSuccess;
  } catch (error) {
    if (error instanceof Response) {
      const body = await error.text();
      return {
        ok: false,
        intent: intentValue,
        error: body || error.statusText || "Workspace action failed",
      } satisfies WorkspaceSidebarActionError;
    }

    return {
      ok: false,
      intent: intentValue,
      error: error instanceof Error ? error.message : "Unknown error",
    } satisfies WorkspaceSidebarActionError;
  }
};
